/**
 * Reset password page (local auth mode only).
 *
 * Expects a `?token=...` query parameter.
 */

import React, { useState, FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/common/Header';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);
    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${apiBase}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || 'Reset failed');
      }

      setSuccess(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-ui-background-primary flex flex-col">
        <Header type="homepage" />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-ui-text-tertiary">Invalid or missing reset token.</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ui-background-primary text-ui-text-primary flex flex-col">
      <Header type="homepage" />

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        {success ? (
          <div className="w-full max-w-sm text-center space-y-4">
            <h2 className="text-2xl font-semibold">Password updated</h2>
            <p className="text-sm text-ui-text-tertiary">
              Your password has been reset. You can now sign in.
            </p>
            <a href="/" className="text-lime-500 hover:underline text-sm">
              Sign in
            </a>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-5">
            <h2 className="text-2xl font-semibold text-center">Set new password</h2>

            {error && (
              <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-400">
                {error}
              </div>
            )}

            <div className="space-y-1">
              <label htmlFor="rp-password" className="block text-sm font-medium text-ui-text-secondary">
                New password
              </label>
              <input
                id="rp-password"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border border-ui-border-primary bg-ui-background-secondary px-3 py-2 text-sm text-ui-text-primary placeholder-ui-text-tertiary focus:outline-none focus:ring-2 focus:ring-lime-400"
                placeholder="Min. 8 characters"
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="rp-confirm" className="block text-sm font-medium text-ui-text-secondary">
                Confirm password
              </label>
              <input
                id="rp-confirm"
                type="password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full rounded-md border border-ui-border-primary bg-ui-background-secondary px-3 py-2 text-sm text-ui-text-primary placeholder-ui-text-tertiary focus:outline-none focus:ring-2 focus:ring-lime-400"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-md bg-lime-500 hover:bg-lime-600 disabled:opacity-50 text-white font-medium py-2 text-sm transition-colors"
            >
              {isSubmitting ? 'Resetting…' : 'Reset password'}
            </button>
          </form>
        )}
      </main>
    </div>
  );
};

export default ResetPasswordPage;
